// Matcher engine. Pure: (sources, parsedQuery) -> flat, display-ordered rows.
//
// Matching rules (v1):
// - case-insensitive substring; every term must match for an item to match
// - table item text: table name
// - column item: every term must hit name/type/table-name, and at least one
//   term must hit the column's own name or type (so a bare table-name query
//   does not flood all of its columns)
// - note item: every term must hit the note's text
// - result ordering is sectioned, not nested: every matched table row comes
//   first (in source order), followed by every matched column row (in
//   source order), followed by every matched note row (in source order),
//   regardless of whether that column's own table matched
// - filters from parseQuery: no handled fields yet — each unhandled filter
//   degrades to a plain term of its raw token
const HANDLED_FILTER_FIELDS = []; // future: 'type', 'col', 'pk', 'nn', …

function includesTerm(text, term) {
    return text.toLowerCase().includes(term);
}

// [start, end) ranges of each term's first occurrence in text, merged and
// sorted — enough for bold-highlighting in the results list.
export function termRanges(text, terms) {
    if (!text) return [];
    const lower = text.toLowerCase();
    const raw = [];
    for (const term of terms) {
        const start = lower.indexOf(term);
        if (start !== -1) raw.push([start, start + term.length]);
    }
    raw.sort((a, b) => a[0] - b[0]);
    const merged = [];
    for (const range of raw) {
        const last = merged[merged.length - 1];
        if (last && range[0] <= last[1]) {
            last[1] = Math.max(last[1], range[1]);
        } else {
            merged.push([...range]);
        }
    }
    return merged;
}

export function runSearch(sources, parsedQuery) {
    const { terms, filters } = parsedQuery;
    const effectiveTerms = [
        ...terms,
        ...filters
            .filter((f) => !HANDLED_FILTER_FIELDS.includes(f.field))
            .map((f) => f.raw),
    ];
    if (!effectiveTerms.length) return [];

    const tableRows = [];
    const columnRows = [];

    for (const source of sources ?? []) {
        for (const table of source.tables) {
            const tableMatched = effectiveTerms.every((term) => includesTerm(table.name, term));

            const matchedColumns = table.columns.filter((column) => {
                const ownHit = (term) => includesTerm(column.name, term) || includesTerm(column.type, term);
                const anyHit = (term) => ownHit(term) || includesTerm(table.name, term);
                return effectiveTerms.every(anyHit) && effectiveTerms.some(ownHit);
            });

            if (!tableMatched && !matchedColumns.length) continue;

            const base = {
                viewId: table.viewId,
                viewTitle: table.viewTitle,
                elementId: table.elementId,
                tableName: table.name,
                bbox: table.bbox,
            };
            if (tableMatched) {
                tableRows.push({
                    ...base,
                    kind: 'table',
                    nameRanges: termRanges(table.name, effectiveTerms),
                });
            }
            columnRows.push(...matchedColumns.map((column) => ({
                ...base,
                kind: 'column',
                columnId: column.columnId,
                columnIndex: column.columnIndex,
                columnName: column.name,
                columnType: column.type,
                flags: column.flags,
                nameRanges: termRanges(column.name, effectiveTerms),
                typeRanges: termRanges(column.type, effectiveTerms),
            })));
        }
    }

    const noteRows = [];
    for (const source of sources ?? []) {
        for (const note of source.notes ?? []) {
            if (!effectiveTerms.every((term) => includesTerm(note.text, term))) continue;
            noteRows.push({
                viewId: note.viewId,
                viewTitle: note.viewTitle,
                elementId: note.elementId,
                kind: 'note',
                bbox: note.bbox,
                noteText: note.text,
                textRanges: termRanges(note.text, effectiveTerms),
            });
        }
    }

    return [...tableRows, ...columnRows, ...noteRows];
}
